/**
 * useKpiCardOrder - Manages KPI card order with localStorage persistence
 * 
 * Allows users to drag/drop KPI cards and saves preference to localStorage
 */

import { useState, useEffect } from 'react';

export type KpiCardType = 'revenue' | 'units' | 'skus' | 'stores';

const DEFAULT_ORDER: KpiCardType[] = ['revenue', 'units', 'skus', 'stores'];
const STORAGE_KEY = 'patterniq_overview_kpi_order';

export function useKpiCardOrder() {
  const [order, setOrder] = useState<KpiCardType[]>(DEFAULT_ORDER);

  // Load saved order from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === 4) {
          setOrder(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading KPI card order:', error);
    }
  }, []);

  // Save order to localStorage whenever it changes
  const updateOrder = (newOrder: KpiCardType[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder));
      setOrder(newOrder);
    } catch (error) {
      console.error('Error saving KPI card order:', error);
    }
  };

  // Reset to default order
  const resetOrder = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setOrder(DEFAULT_ORDER);
    } catch (error) {
      console.error('Error resetting KPI card order:', error);
    }
  };

  return {
    order,
    updateOrder,
    resetOrder,
    isCustomOrder: JSON.stringify(order) !== JSON.stringify(DEFAULT_ORDER)
  };
}

